import { useMemo, useState } from "react";

import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { InvitationPopup } from "@/Common/Components/Portal/InvitationPopup/InvitationPopup";
import { PortalStatusButton, PortalStatusIcon } from "@/Common/Components/Portal/PortalStatusIcon";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { Person } from "@/Common/Models/Person/Person";
import { GenderLookup } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { MatchingCriteriaService } from "@/Common/Services/PatientSearchMatching/MatchingCriteriaService";
import { sessionStore } from "@/Globals/Stores/SessionStore";
import { StatusComponent } from "../Patient/StatusComponent";

import img_patient_icon from "@/Common/_images/patient-icon.png";
import img_relative_icon from "@/Common/_images/relative-icon.png";

interface IndividualAdvancedSearchResultProps {
    person: Person;
    currentPersonContext?: Person;
    rootClassName: string;
    hideIndividualImage?: boolean;
    onClick?: () => void;
    onRemove?: () => void;
}

function IndividualAdvancedSearchResult(props: IndividualAdvancedSearchResultProps): JSX.Element {
    const [invitationPopupOpen, setInvitationPopupOpen] = useState<boolean>(false);

    const person = props.person;
    const address = person.GetFormattedAddress();
    const nextApp = person.Patient?.GetNextAppointmentFormatted();
    const dateOfBirth = person.GetFormattedDateOfBirth();
    const phone1 = person.GetFormattedPhonesByPriorityWithPosition(0);
    const phone2 = person.GetFormattedPhonesByPriorityWithPosition(1);
    const phone3 = person.GetFormattedPhonesByPriorityWithPosition(2);
    const genderLookup = cacheStore.GetLookupById(person.GenderLookupId);
    const patientRenderColumn2: React.CSSProperties = { justifyContent: "flex-start", width: "100%" };
    const patientRenderColumn2Div1: React.CSSProperties = { width: "50%" };
    const patientRenderColumn2Div2: React.CSSProperties = { width: "50%" };
    const physicianName = person.Patient?.GetPhysicianNameByProvince();

    const currentPerson = props.currentPersonContext;
    const matches = useMemo(() => MatchingCriteriaService.GetMatchingCriteriaForPerson(currentPerson, person), [currentPerson, person]);
    const highlightClass = "highlight-yellow";

    let portalButton: JSX.Element = null;

    if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite) {
        const buttonProps = PortalHelper.getPortalButtonPropsForRegistrationState(person.PortalSyncState);
        if (buttonProps) {
            portalButton = (cacheStore.ClinicSettings.EnablePortalInvitationFlowV2 ? (
                <div className="margin-xs-top">
                    <PortalStatusButton
                        onClick={() => setInvitationPopupOpen(true)}
                        {...buttonProps}
                    />
                </div>
            ) : (
                <div className="margin-xs-top"><PortalStatusIcon {...buttonProps} /></div>
            ));
        }
    }

    function getPersonIcon(person: Person): string {
        return person.ContactFacetType === ContactFacetType.Patient ? img_patient_icon : img_relative_icon;
    }

    const externalSearchIdentifiersForActiveSite = props.person.Patient?.GetVisibleExternalIdentifersForActiveSite() ?? [];
    return (
        <div className={props.rootClassName} onClick={() => props.onClick?.()}>
            {props.hideIndividualImage
                ? (
                    <div style={{ width: "5px" }} />
                ) : (
                    <div className="flex-col r1">
                        <img title={person.NameWithIdentifier} src={getPersonIcon(person)} style={{ width: "48px", paddingTop: "5px" }} alt={person.NameWithIdentifier} />
                        {portalButton}
                    </div>
                )
            }
            <div className="flex-container" style={patientRenderColumn2}>
                <div className="flex-col" style={patientRenderColumn2Div1}>
                    <h4 className="light-blue margin-none-top"><span className={matches?.firstName || matches?.lastName ? highlightClass : ""}>{person.Name}</span>
                        {genderLookup && genderLookup.LookupCode !== GenderLookup.Other && (
                            <span className={`myle-icon-16 ${genderLookup.LookupCode.toLowerCase()}`} />
                        )}
                    </h4>
                    <ul>
                        <li><strong>{sessionStore.getDefaultHealthCardLabel()} </strong><span><span className={matches?.healthCardNumber ? highlightClass : ""}>{person.GetFormattedNAM()}</span> {person.GetFormattedNAMExpirationDateString()}</span></li>
                        <li><strong>{cacheStore.GetString("FileNumbersLabel")} </strong><span className={matches?.identifier01 ? highlightClass : ""}>{person.Patient?.PatientResponse.Identifier01}</span></li>
                        <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong><span className={matches?.dateOfBirth ? highlightClass : ""}>{`${dateOfBirth} ${person.GetFormattedAge()}`}</span></li>
                        <li className="flex-container">
                            <strong>{cacheStore.GetString("PhysicianNameLabel")}</strong>
                            <span>
                                <StatusComponent
                                    status={person.Patient?.GetAssignedDoctorStatus()}
                                    statusInfo={physicianName}
                                />
                            </span>
                        </li>
                        <li>
                            {cacheStore.ClinicSettings.EnableExternalPatientSearch && props.person.Patient && (
                                <div>
                                    {externalSearchIdentifiersForActiveSite.map(extIdent => (
                                        <div
                                            key={`extIdent_${extIdent.SystemCode}_${extIdent.Number}`}
                                            className="flex-container"
                                            style={{ justifyContent: "flex-start" }}
                                            title={`${props.person.Patient.GetVisibleExternalIdentifers().map(p => `${p.ShortName}-${p.Number}`).join("\r\n")}`}
                                        >
                                            <div>
                                                <span className="glyphicon glyphicon-ok green-text" />
                                            </div>
                                            <div>
                                                <a onClick={() => sessionStore.OpenExternalSearchPopup(props.person.Patient)} style={{ whiteSpace: "nowrap", marginLeft: "3px" }}>
                                                    {`${extIdent.ShortName}-${extIdent.Number}`}
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                    {!externalSearchIdentifiersForActiveSite.any() &&
                                            cacheStore.GetPatientExternalSearchSystemCodesForActiveSite().map(systemCode => (
                                                <div
                                                    key={`extIdent_${systemCode}`}
                                                    className="flex-container"
                                                    style={{ justifyContent: "flex-start" }}
                                                    onClick={e => e.stopPropagation()}
                                                    title={`${props.person.Patient.GetVisibleExternalIdentifers().map(p => `${p.ShortName}-${p.Number}`).join("\r\n")}`}
                                                >
                                                    <div><span className="glyphicon glyphicon-remove red-text" /></div>
                                                    <div>
                                                        <a onClick={() => sessionStore.OpenExternalSearchPopup(props.person.Patient)} style={{ whiteSpace: "nowrap", marginLeft: "3px" }}>
                                                            {systemCode}
                                                        </a>
                                                    </div>
                                                </div>
                                            ))
                                    }
                                </div>
                            )}
                        </li>
                    </ul>

                </div>

                <div className="flex-col" style={patientRenderColumn2Div2}>
                    <ul>
                        {phone1 != "" && <li><span className="myle-icon-16 phone" /><span className={`content-text ${matches?.phone1 ? highlightClass : ""}`}>{phone1}</span></li>}
                        {phone2 != "" && <li><span className="myle-icon-16 phone" /><span className={`content-text ${matches?.phone2 ? highlightClass : ""}`}>{phone2}</span></li>}
                        {phone3 != "" && <li><span className="myle-icon-16 phone" /><span className={`content-text ${matches?.phone3 ? highlightClass : ""}`}>{phone3}</span></li>}
                        {address != "" && <li><span className="myle-icon-16 pin" /><span className={`content-text ${matches?.address ? highlightClass : ""}`}>{address}</span></li>}
                        {nextApp != "" && <li><span className="myle-icon-16 none" /><span className="content-text">{nextApp}</span></li>}
                    </ul>
                </div>
            </div>
            {props.onRemove && (
                <div>
                    <RemoveButton
                        onRemove={() => props.onRemove?.()}
                    />
                </div>
            )}
            {invitationPopupOpen && (
                <InvitationPopup
                    person={props.person}
                    onCancel={() => setInvitationPopupOpen(false)}
                />
            )}
        </div>
    );
}
export { IndividualAdvancedSearchResult };
